import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserDisplay } from '../hooks/useUserDisplay';
import { X, Mail, Lock, User, Eye, EyeOff, CheckCircle, AlertCircle, Shield, Package, Zap } from 'lucide-react';
import { 
  authStorage, 
  validateEmail, 
  validatePassword, 
  getPasswordStrengthColor, 
  getPasswordStrengthText,
  getAuthErrorMessage 
} from '../utils/authUtils';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode = 'login',
}) => {
  const { signIn, signUp, loading } = useAuth();
  const [mode, setMode] = useState<'login' | 'register' | 'email-sent'>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [rememberCredentials, setRememberCredentials] = useState(true);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong'>('weak');
  const [isSupplierRequested, setIsSupplierRequested] = useState(false);

  // Carregar credenciais salvas e verificar retorno de confirmação
  useEffect(() => {
    if (isOpen) {
      const savedData = authStorage.getSavedData();
      const pendingVerification = authStorage.hasPendingVerification();
      
      if (mode === 'login' && savedData.email) {
        setFormData(prev => ({
          ...prev,
          email: savedData.email,
          fullName: savedData.fullName
        }));
      }

      // Se há verificação pendente, apenas limpa para evitar bloqueio
      if (pendingVerification && savedData.lastAction === 'register') {
        try {
          authStorage.clearPendingVerification();
        } catch {}
      }
      setIsSupplierRequested(false);
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailError(null);

    // Validações antes de enviar
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      setEmailError(emailValidation.message || 'Email inválido');
      return;
    }

    if (mode === 'register') {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.message || 'Senha inválida');
        return;
      }
    }

    try {
      let result;
      if (mode === 'login') {
        result = await signIn(formData.email, formData.password);
        
        if (result.error) {
          setError(getAuthErrorMessage(result.error));
        } else {
          // Salvar credenciais se solicitado
          if (rememberCredentials) {
            authStorage.saveTemporaryData(formData.email, formData.fullName, 'login');
          } else {
            authStorage.setLastAction('login');
          }
          
          // Aguardar um pouco para garantir que o estado foi atualizado
          setTimeout(() => {
            onClose();
            setFormData({ email: '', password: '', fullName: '' });
            setError(null);
          }, 300);
        }
      } else if (mode === 'register') {
        // Registro
        result = await signUp(formData.email, formData.password, formData.fullName, isSupplierRequested);
        
        if (result.error) {
          setError(getAuthErrorMessage(result.error));
        } else {
          // Salvar dados
          if (rememberCredentials) {
            authStorage.saveTemporaryData(formData.email, formData.fullName, 'register');
          }
          
          // Fechar o modal diretamente
          setTimeout(() => {
            onClose();
            setFormData({ email: '', password: '', fullName: '' });
            setError(null);
          }, 300);
        }
      }
    } catch (err) {
      setError('Ocorreu um erro inesperado. Tente novamente.');
    }
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError(null);
    setEmailError(null);
  };

  // Validação em tempo real do email
  const handleEmailChange = (email: string) => {
    setFormData(prev => ({ ...prev, email }));
    if (email) {
      const validation = validateEmail(email);
      setEmailError(validation.isValid ? null : validation.message || null);
    } else {
      setEmailError(null);
    }
  };

  // Validação em tempo real da senha
  const handlePasswordChange = (password: string) => {
    setFormData(prev => ({ ...prev, password }));
    if (password && mode === 'register') {
      const validation = validatePassword(password);
      setPasswordStrength(validation.strength);
    }
  };

  const handleResendEmail = async () => {
    if (formData.email) {
      try {
        await signUp(formData.email, formData.password, formData.fullName);
        setError(null);
      } catch (err) {
        setError('Erro ao reenviar email. Tente novamente.');
      }
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'login': return 'Entrar';
      case 'register': return 'Criar Conta';
      case 'email-sent': return 'Confirme seu Email';
      default: return 'Entrar';
    }
  };

  // Tela de confirmação de email
  if (mode === 'email-sent') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative bg-[#1a1d21] rounded-3xl border border-gray-700 p-8 w-full max-w-md mx-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Confirme seu Email</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-[#2a2d31]"
            >
              <X size={20} />
            </button>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-[#0FFF55]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-[#0FFF55]" />
            </div>
            
            <h3 className="text-xl font-bold text-white mb-2">Email Enviado!</h3>
            <p className="text-gray-300 mb-4">
              Enviamos um link de confirmação para:
            </p>
            <p className="text-[#0FFF55] font-semibold mb-6 bg-[#0FFF55]/10 px-4 py-2 rounded-xl">
              {formData.email}
            </p>
            
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-left">
                  <p className="text-blue-300 text-sm font-semibold mb-1">Próximo passo:</p>
                  <p className="text-blue-200 text-sm">
                    Clique no link do email para ativar sua conta. Após clicar, você será automaticamente logado na plataforma.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleResendEmail}
                disabled={loading}
                className="w-full bg-[#2a2d31] text-white font-medium py-3 rounded-2xl hover:bg-[#3a3d41] transition-all duration-300 disabled:opacity-50"
              >
                {loading ? 'Reenviando...' : 'Reenviar Email'}
              </button>

              <button
                onClick={() => {
                  // Usuário já confirmou: limpar estado pendente e voltar ao login
                  try {
                    authStorage.clearPendingVerification();
                    authStorage.setLastAction('login');
                  } catch {}
                  setMode('login');
                }}
                className="w-full text-gray-400 hover:text-[#0FFF55] transition-colors text-sm"
              >
                Já confirmei meu email - Fazer Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-[#1a1d21] rounded-3xl border border-gray-700 p-8 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">
            {getTitle()}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-[#2a2d31]"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nome Completo
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full bg-[#121316] text-white pl-10 pr-4 py-3 rounded-2xl border border-gray-600 focus:border-[#0FFF55] focus:outline-none transition-colors"
                  placeholder="Digite seu nome completo"
                  required={mode === 'register'}
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className={`w-full bg-[#121316] text-white pl-10 pr-12 py-3 rounded-2xl border transition-colors ${
                  emailError 
                    ? 'border-red-500 focus:border-red-500' 
                    : formData.email && !emailError
                    ? 'border-green-500 focus:border-green-500'
                    : 'border-gray-600 focus:border-[#0FFF55]'
                } focus:outline-none`}
                placeholder="Digite seu email"
                required
              />
              {/* Indicador visual de validação */}
              {formData.email && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {emailError ? (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </div>
              )}
            </div>
            {emailError && (
              <p className="text-red-400 text-sm mt-2 flex items-center space-x-2">
                <AlertCircle size={14} />
                <span>{emailError}</span>
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-300">
                Senha
              </label>
              {mode === 'register' && formData.password && (
                <span className={`text-xs font-medium ${getPasswordStrengthColor(passwordStrength)}`}>
                  Força: {getPasswordStrengthText(passwordStrength)}
                </span>
              )}
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                className="w-full bg-[#121316] text-white pl-10 pr-12 py-3 rounded-2xl border border-gray-600 focus:border-[#0FFF55] focus:outline-none transition-colors"
                placeholder="Digite sua senha"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {/* Barra de força da senha para registro */}
            {mode === 'register' && formData.password && (
              <div className="mt-2">
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength === 'weak' ? 'w-1/3 bg-red-500' :
                      passwordStrength === 'medium' ? 'w-2/3 bg-yellow-500' :
                      'w-full bg-green-500'
                    }`}
                  />
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Checkbox Lembrar Dados / Fornecedor */}
          <div className="flex flex-col space-y-3">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="rememberCredentials"
                checked={rememberCredentials}
                onChange={(e) => setRememberCredentials(e.target.checked)}
                className="w-4 h-4 text-[#0FFF55] bg-[#121316] border-gray-600 rounded focus:ring-[#0FFF55] focus:ring-2"
              />
              <label htmlFor="rememberCredentials" className="text-sm text-gray-300 cursor-pointer">
                {mode === 'login' ? 'Lembrar email' : 'Lembrar dados para facilitar login'}
              </label>
            </div>

            {mode === 'register' && (
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isSupplierRequested"
                  checked={isSupplierRequested}
                  onChange={(e) => setIsSupplierRequested(e.target.checked)}
                  className="w-4 h-4 text-[#0FFF55] bg-[#121316] border-gray-600 rounded focus:ring-[#0FFF55] focus:ring-2"
                />
                <label htmlFor="isSupplierRequested" className="text-sm text-gray-300 cursor-pointer">
                  Quero me cadastrar como fornecedor oficial
                </label>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold py-3 rounded-2xl transition-all duration-300 hover:shadow-lg hover:shadow-[#0FFF55]/30 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? 'Carregando...' : mode === 'login' ? 'Entrar' : 'Criar Conta'}
          </button>
        </form>

        {/* Toggle Mode */}
        <div className="mt-6 text-center">
          <button
            onClick={toggleMode}
            className="text-gray-400 hover:text-[#0FFF55] transition-colors text-sm"
          >
            {mode === 'login' 
              ? 'Não tem uma conta? Cadastre-se' 
              : 'Já tem uma conta? Faça login'
            }
          </button>
        </div>
      </div>
    </div>
  );
};

interface UserMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAuth: () => void;
  onNavigate?: (section: string) => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ 
  isOpen, 
  onClose, 
  onOpenAuth,
  onNavigate
}) => {
  const { user, profile, signOut, isAdmin } = useAuth();
  const { displayName, avatarInitial, avatarUrl, avatarKey, email } = useUserDisplay();

  if (!isOpen) return null;

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute top-20 right-6 bg-[#1a1d21] rounded-2xl border border-gray-700 shadow-xl min-w-[250px] p-4">
        {user ? (
          <div className="space-y-4">
            {/* User Info */}
            <div className="border-b border-gray-700 pb-4">
              <div className="flex items-center space-x-3">
                <Avatar className="w-10 h-10 ring-2 ring-[#0FFF55]/30" key={avatarKey}>
                  <AvatarImage src={avatarUrl} key={`${avatarUrl}-${avatarKey}`} />
                  <AvatarFallback className="bg-gradient-to-br from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold text-sm">
                    {avatarInitial}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-white font-semibold">
                    {displayName}
                  </p>
                  <p className="text-gray-400 text-sm">{email}</p>
                  {isAdmin && (
                    <span className="inline-block bg-[#0FFF55] text-[#121316] text-xs px-2 py-1 rounded-full font-bold mt-1">
                      ADMIN
                    </span>
                  )}
                </div>
              </div>
            </div>


            {/* Menu Items */}
            <div className="space-y-1 pt-1">
              <button 
                onClick={() => {
                  onNavigate?.('my-products');
                  onClose();
                }}
                className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-[#2a2d31] rounded-lg transition-colors text-sm"
              >
                Minhas Compras
              </button>
              <button 
                onClick={() => {
                  onNavigate?.('profile');
                  onClose();
                }}
                className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-[#2a2d31] rounded-lg transition-colors text-sm"
              >
                Meu Perfil
              </button>
              <button 
                onClick={() => {
                  onNavigate?.('integrations');
                  onClose();
                }}
                className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-[#2a2d31] rounded-lg transition-colors text-sm flex items-center justify-between"
              >
                <span>Integrações Master Shop</span>
                <span className="text-[10px] bg-[#0FFF55]/20 text-[#0FFF55] font-bold px-1.5 py-0.5 rounded">API</span>
              </button>
              {isAdmin && (
                <button 
                  onClick={() => {
                    onNavigate?.('admin');
                    onClose();
                  }}
                  className="w-full text-left px-3 py-2 text-[#0FFF55] hover:text-white hover:bg-[#2a2d31] rounded-lg transition-colors font-semibold text-sm"
                >
                  Painel Admin
                </button>
              )}
            </div>

            {/* Sign Out */}
            <div className="border-t border-gray-700 pt-4">
              <button
                onClick={handleSignOut}
                className="w-full text-left px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                Sair
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <User className="text-gray-400" size={20} />
              </div>
              <p className="text-white font-semibold">Olá, Convidado!</p>
              <p className="text-gray-400 text-sm">Faça login para acessar mais recursos</p>
            </div>
            
            <button
              onClick={() => {
                onClose();
                onOpenAuth();
              }}
              className="w-full bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold py-2 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#0FFF55]/30"
            >
              Fazer Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};